import type { LoginRequest, SignupRequest, AuthResponse } from '@madboat/types';

/**
 * Auth API Client
 * Communicates with Cloudflare Workers backend
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';
const MKEY_API_URL = import.meta.env.VITE_MKEY_API_URL || 'https://mkey-api-production.fidelismadboat.workers.dev';

class AuthAPIError extends Error {
  constructor(public statusCode: number, message: string) {
    super(message);
    this.name = 'AuthAPIError';
  }
}

async function fetchAPI<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new AuthAPIError(
      response.status,
      error.error || `Request failed with status ${response.status}`
    );
  }

  return response.json();
}

/**
 * Login with email and password
 */
export async function login(data: LoginRequest): Promise<AuthResponse> {
  return fetchAPI<AuthResponse>('/auth/login', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Signup with email, password, and name
 */
export async function signup(data: SignupRequest): Promise<AuthResponse> {
  return fetchAPI<AuthResponse>('/auth/signup', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Get current user (requires JWT token)
 */
export async function getCurrentUser(token: string): Promise<AuthResponse> {
  return fetchAPI<AuthResponse>('/auth/me', {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

/**
 * Send heartbeat to keep session alive (mkey worker owns sessions)
 */
export async function sendHeartbeat(token: string): Promise<{ success: boolean }> {
  const url = `${MKEY_API_URL}/session/heartbeat`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new AuthAPIError(
      response.status,
      error.error || `Request failed with status ${response.status}`
    );
  }

  return response.json();
}

/**
 * Logout (client-side only - clears token)
 */
export function logout(): void {
  // Token will be cleared by authStore
  // No API call needed (stateless JWT)
}
